import {EntityState, EntityStore, QueryEntity, selectPersistStateInit, StoreConfig} from '@datorama/akita';
import {Injectable} from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import {ItemQuery} from './models/item.query';
import {
  getIdsInArrayWrapper,
  IExpiringStore,
  MesHttpStatusHandlerService,
  removeRedundantKeys, StoreHttpService
} from 'common-shared';
import {filter, map, tap} from 'rxjs/operators';
import {plainToInstance} from 'class-transformer';

interface State extends EntityState<ItemQuery> {
}

@Injectable({
  providedIn: 'root'
})
@StoreConfig({name: 'Item', idKey: 'Id'})
export class ItemsStore extends EntityStore<State, ItemQuery> implements IExpiringStore {
  expire$: Subject<string | string[] | null> = new Subject();

  constructor(private mesHttpStatusHandlerService: MesHttpStatusHandlerService, private storeHttpService: StoreHttpService) {
    super();

    mesHttpStatusHandlerService.cacheExpire$
      .pipe(filter(value => value.region?.toLocaleLowerCase() === this.storeName.toLocaleLowerCase()))
      .subscribe(value => {
        if (value.id && value.entities) {
          this.remove(value.id);
          let entities = removeRedundantKeys(value.entities, ItemQuery.Columns());
          this.add(plainToInstance(ItemQuery, entities));
        } else {
          this.expire$.next(value.id);
        }
      });

    mesHttpStatusHandlerService.cacheRemove$
      .pipe(filter(value => value.region?.toLocaleLowerCase() === this.storeName.toLocaleLowerCase()))
      .subscribe(value => this.remove(value.id));

    selectPersistStateInit().subscribe(() => {
      if (!this.getValue().ids.length) {
        this.loadAll().subscribe();
      }
    });
    this.expire$.subscribe(value => {
      return this.loadAll(value).subscribe();
    });
  }

  loadAll(ids: string | string[] | null = null) {
    this.setHasCache(false);
    this.setLoading(true);
    const idsFilter = getIdsInArrayWrapper(ids);
    const request$ = this.storeHttpService.addGet<{ items: string[] }, ItemQuery[]>(idsFilter, {
      action: 'GetByIds',
      controller: this.storeName,
    }).pipe(map(value => plainToInstance(ItemQuery, value.data)),
      tap((items: ItemQuery[]) => {
          if (idsFilter.items.length) {
            this.remove(items.map(value => value.Id));
            this.add(items);
          } else {
            this.set(items);
          }
          this.setHasCache(true);
          this.setLoading(false);
        }
      ));
    return this.cache.active.getValue() ? of() : request$;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ItemsQuery extends QueryEntity<State, ItemQuery> {
  constructor(protected store: ItemsStore) {
    super(store);
  }

  loadAll = (ids: string | string[] | null = null) => this.store.loadAll(ids);

  getAll$: Observable<ItemQuery[]> = this.selectAll();
  get$ = (id: string) => this.selectEntity(id);
}
